/**
 * Folder-driven asset discovery for the Character Editor (shared by the head
 * editor, the Animation preview tab and the photo-analysis prompt).
 *
 * Extracted from DebugCharacterEditorScreen.tsx (2026-07-29) so every consumer
 * reads the SAME `import.meta.glob` catalogue — one source of truth. The dev-only
 * folder sanity check runs once when this module is first imported.
 *
 * Assets are organised **per character** (Tamara, 2026-07-31 — she moved the
 * existing folders under `Child/` and added `Dad/`), so Dad's hair can diverge
 * from the child's instead of both wearing the same PNG:
 *
 *   assets/<Character>/hair-front/<Style>/<Style>_01.png … _0N.png
 *   assets/<Character>/hair-back/<Style>/<Style>_01.png … _0N.png
 *   assets/<Character>/beard/<Style>/<Style>_01.png … _0N.png      (Dad only)
 *   assets/<Character>/pupils/<EyeColour>.png
 *   assets/<Character>/freckles/*.png
 *   assets/<Character>/glasses/*.png
 *
 * `<Character>` matches a {@link CharacterId} case-insensitively (`Child`, `Dad`,
 * `Mom`), so adding Mom's folder needs no code change here.
 */
import { CHARACTERS, type CharacterId } from './animationContract';
import {
  EYE_PUPIL_ASSET,
  FRECKLES_ASSET,
  GLASSES_ASSET,
  EYE_COLOURS,
  HAIR_COLOURS,
  HAIR_LAYER_COUNT,
  HAIR_SETS_URL,
  SKIN_SETS_URL,
} from './contract';
import { create } from 'zustand';
import { seedShades } from './colorUtils';
import type { HairLayer } from './hairCompositor';
import { CHARACTER_SKIN_PALETTE } from './characterPalette';

// Folder-driven LAYERED hair, organised STYLE → ordered layers:
//   assets/<Character>/hair-front/<Style>/<Style>_01.png … _0N.png  (and hair-back/…)
// One grayscale shape set per hairstyle, recoloured in code (see hairCompositor)
// — no per-colour folders. `_01` = bottom layer … `_0N` = top. A style gallery
// picks the hairstyle; the 8-swatch + per-layer HSB pickers choose the colour.
// Drop files in → they appear (Vite re-globs on add in dev; baked at build).
// We read glob KEYS and strip `/public` to get served URLs (never import public
// assets — that yields a broken `/public/…` URL).
export interface LayeredHair {
  /**
   * styleName → layers ordered bottom→top by their `_NN` suffix, each carrying that
   * number so the compositor can shade it BY NAME rather than by position (a stack
   * missing `_02` then skips shade 2 instead of shifting the whole ramp).
   */
  byStyle: Record<string, HairLayer[]>;
  /** all style folder names, sorted (the style gallery). */
  styles: string[];
}

/** A flat folder of swappable images (freckles / glasses / pupils). */
export interface FlatFolder {
  /** file stems, sorted (the picker). */
  stems: string[];
  /** stem → served URL. */
  byStem: Record<string, string>;
}

/** Everything one character can wear, read from `assets/<Character>/…`. */
export interface CharacterAssets {
  /** The folder name on disk (`Child`), for hints and warnings. */
  folder: string;
  hairFront: LayeredHair;
  hairBack: LayeredHair;
  /** Hairstyle names present in either layer. */
  hairStyles: string[];
  /**
   * Facial hair — the same layered-and-recoloured shape as the hair pair, so it
   * goes through `buildRecoloredHair` with a ramp of its own. Only characters with
   * a `beard/` folder have any (Dad today), and an empty catalogue is what hides
   * the beard controls for everyone else — no per-character list in code.
   */
  beard: LayeredHair;
  freckles: FlatFolder;
  glasses: FlatFolder;
  /** Pupils are NAMED BY EYE COLOUR (Blue.png, Green.png … matching EYE_COLOURS). */
  pupils: FlatFolder;
}

// ── One glob for the whole tree, partitioned per character ────────────────────
// `import.meta.glob` needs a literal pattern, so a per-character glob would mean
// one literal per character × kind. Globbing the tree once and splitting on the
// path instead means a new character folder (`Mom/`) just works.
const ASSET_ROOT = '/assets/rive/sb_character_editor/assets/';
const ASSET_MODULES = import.meta.glob(
  '/assets/rive/sb_character_editor/assets/**/*.{png,jpg,jpeg,webp}',
);

/** One discovered file: which character + kind folder it sits in, and where. */
interface AssetEntry {
  /** Path segments BELOW the kind folder — `["Bob Neck", "BobNeck_01.png"]`. */
  rest: string[];
  /** Served URL (`/public` stripped). */
  url: string;
}
/** characterFolder(lowercased) → kind(lowercased) → files. */
type AssetTree = Record<string, Record<string, AssetEntry[]>>;

let ASSET_TREE: AssetTree = {};
/** Top-level folders that aren't a known character — reported in dev. */
let STRAY_FOLDERS = new Set<string>();

/**
 * Sort one `<Character>/<kind>/<…>/<file>` path into the tree.
 *
 * Shared by the build-time glob and the runtime re-read (see
 * {@link refreshCharacterAssets}), so both produce an identical catalogue — the
 * refresh can't drift from what a page load would have discovered.
 */
function addToTree(tree: AssetTree, strays: Set<string>, relPath: string, url: string): void {
  const segs = relPath.split('/');
  // Need at least <Character>/<kind>/<file>. Anything shallower is a leftover
  // from the pre-per-character layout and is reported rather than guessed at.
  if (segs.length < 3) {
    strays.add(segs[0] ?? '');
    return;
  }
  const [character, kind, ...rest] = segs;
  const known = CHARACTERS.some((c) => c.id === character.toLowerCase());
  if (!known) {
    strays.add(character);
    return;
  }
  const byKind = (tree[character.toLowerCase()] ??= {});
  (byKind[kind.toLowerCase()] ??= []).push({ rest, url });
}

for (const key of Object.keys(ASSET_MODULES)) {
  if (!key.startsWith(ASSET_ROOT)) continue;
  addToTree(ASSET_TREE, STRAY_FOLDERS, key.slice(ASSET_ROOT.length), key.replace(/^\/assets/, ''));
}

const EMPTY_LAYERED: LayeredHair = { byStyle: {}, styles: [] };
const EMPTY_FLAT: FlatFolder = { stems: [], byStem: {} };

/** `<Style>/<Style>_NN.png` files → style → layers ordered bottom→top. */
const parseLayeredHair = (entries: AssetEntry[] | undefined): LayeredHair => {
  const byStyle: Record<string, HairLayer[]> = {};
  for (const { rest, url } of entries ?? []) {
    if (rest.length < 2) continue; // require <Style>/<file>
    const style = rest[0];
    const fileName = rest[rest.length - 1];
    // Require an "_NN" layer suffix (e.g. Afro_03.png → 3). This names the layer —
    // it sets the stacking order AND picks the shade (see HairLayer) — and it
    // excludes the retired flat per-colour PNGs (e.g. "Buzz V1.png") that may
    // still sit in an old folder.
    const m = fileName.match(/_(\d+)(?=\.[^.]+$)/);
    if (!m) continue;
    (byStyle[style] ??= []).push({ n: parseInt(m[1], 10), url });
  }
  for (const layers of Object.values(byStyle)) layers.sort((a, b) => a.n - b.n);
  return { byStyle, styles: Object.keys(byStyle).sort() };
};

// Flat folder-swappable overlays (freckles / glasses / pupils): drop image(s) in
// the folder and they're used — first file (alphabetical) is the default,
// multiple files show a picker, the "—" tile restores the .riv's built-in image.
const parseFlatFolder = (entries: AssetEntry[] | undefined): FlatFolder => {
  const byStem: Record<string, string> = {};
  for (const { rest, url } of entries ?? []) {
    const fileName = rest[rest.length - 1] ?? '';
    byStem[fileName.replace(/\.[^.]+$/, '')] = url;
  }
  return { stems: Object.keys(byStem).sort(), byStem };
};

const buildCharacterAssets = (id: CharacterId): CharacterAssets => {
  const kinds = ASSET_TREE[id] ?? {};
  const hairFront = parseLayeredHair(kinds['hair-front']);
  const hairBack = parseLayeredHair(kinds['hair-back']);
  return {
    folder: `${id.charAt(0).toUpperCase()}${id.slice(1)}`,
    hairFront,
    hairBack,
    hairStyles: [...new Set([...hairFront.styles, ...hairBack.styles])].sort(),
    beard: parseLayeredHair(kinds.beard),
    freckles: parseFlatFolder(kinds.freckles),
    glasses: parseFlatFolder(kinds.glasses),
    pupils: parseFlatFolder(kinds.pupils),
  };
};

/**
 * Every character's own catalogue. A character with no folder on disk gets empty
 * catalogues — its pickers show the "drop images in …" hint instead of the
 * child's art, which is the honest state.
 *
 * The OBJECT IDENTITY is stable; {@link refreshCharacterAssets} replaces the
 * per-character entries in place. That's what lets a folder re-read reach every
 * consumer (both tabs read `CHARACTER_ASSETS[id]` during render) without any of
 * them re-importing or remounting.
 */
export const CHARACTER_ASSETS: Record<CharacterId, CharacterAssets> = Object.fromEntries(
  CHARACTERS.map((c) => [c.id, buildCharacterAssets(c.id)]),
) as Record<CharacterId, CharacterAssets>;

// ── Runtime folder re-read ("Reload folders") ─────────────────────────────────
/**
 * Bumped every time the catalogue is rebuilt. Components subscribe with
 * {@link useAssetCatalogVersion} to re-render, and the two tabs fold it into their
 * per-slot composite signature so the CURRENTLY selected style is re-composited —
 * that's what makes a PNG whose contents changed under the same filename appear.
 */
interface AssetCatalogState {
  version: number;
  bump: () => void;
}
const useAssetCatalogStore = create<AssetCatalogState>((set) => ({
  version: 0,
  bump: () => set((s) => ({ version: s.version + 1 })),
}));

/** Subscribe to catalogue rebuilds (re-renders on "Reload folders"). */
export const useAssetCatalogVersion = (): number => useAssetCatalogStore((s) => s.version);

/** What a refresh found, for the status line. */
export interface AssetRefreshResult {
  ok: boolean;
  /** Total image files discovered. */
  files: number;
  /** Style names added since the last catalogue, `<kind>/<Style>` per character. */
  added: string[];
  /** Style names that disappeared. */
  removed: string[];
  /** Set when the re-read couldn't happen (no dev server). */
  error?: string;
}

/** `hair-front`/`hair-back`/`beard` style names of one catalogue, for diffing. */
const styleKeys = (assets: Record<CharacterId, CharacterAssets>): Set<string> => {
  const out = new Set<string>();
  for (const c of CHARACTERS) {
    const a = assets[c.id];
    for (const [kind, layered] of [['hair-front', a.hairFront], ['hair-back', a.hairBack], ['beard', a.beard]] as const) {
      for (const style of layered.styles) out.add(`${a.folder}/${kind}/${style}`);
    }
  }
  return out;
};

/**
 * Re-read the asset folders from disk and rebuild the catalogue **in place**,
 * without a page reload.
 *
 * `import.meta.glob` is resolved once at module load, so dropping in a PNG is
 * otherwise invisible until the page reloads — and a reload costs the pose sliders,
 * the selected character and every unsaved colour edit (Tamara, 2026-08-05: "only
 * reloads hair folders — without changing anything else on screen"). This asks the
 * dev server for the tree as it is NOW (`/__list-character-assets`) and rebuilds
 * from it, using the SAME `addToTree` / `buildCharacterAssets` path a page load
 * takes, so the result can't drift from it.
 *
 * Every URL gets a `?v=<mtimeMs>` cache-buster, which is what makes a file whose
 * CONTENTS changed under the same name actually re-fetch and re-composite.
 *
 * Selections are deliberately NOT touched: a style that vanished stays selected and
 * simply composites to nothing, rather than this silently re-picking for you.
 * Returns `ok: false` off the dev server, where the endpoint doesn't exist.
 */
export async function refreshCharacterAssets(): Promise<AssetRefreshResult> {
  const before = styleKeys(CHARACTER_ASSETS);
  let files: { path: string; mtimeMs: number }[];
  try {
    const res = await fetch('/__list-character-assets', { cache: 'no-store' });
    if (!res.ok) {
      return { ok: false, files: 0, added: [], removed: [], error: `HTTP ${res.status}` };
    }
    const json = (await res.json()) as { files?: unknown };
    if (!Array.isArray(json.files)) {
      return { ok: false, files: 0, added: [], removed: [], error: 'unexpected response' };
    }
    files = json.files.filter(
      (f): f is { path: string; mtimeMs: number } =>
        !!f && typeof (f as { path?: unknown }).path === 'string',
    );
  } catch {
    return { ok: false, files: 0, added: [], removed: [], error: 'dev server only' };
  }

  const tree: AssetTree = {};
  const strays = new Set<string>();
  for (const f of files) {
    // `encodeURI` is applied by the loaders, not here — the tree stores raw paths
    // (folder names contain spaces) exactly as the glob does.
    const v = f.mtimeMs ? `?v=${f.mtimeMs}` : '';
    addToTree(tree, strays, f.path, `${ASSET_ROOT.replace(/^\/assets/, '')}${f.path}${v}`);
  }
  ASSET_TREE = tree;
  STRAY_FOLDERS = strays;
  for (const c of CHARACTERS) CHARACTER_ASSETS[c.id] = buildCharacterAssets(c.id);

  const after = styleKeys(CHARACTER_ASSETS);
  const added = [...after].filter((k) => !before.has(k)).sort();
  const removed = [...before].filter((k) => !after.has(k)).sort();
  useAssetCatalogStore.getState().bump();
  if (import.meta.env.DEV) {
    const issues = checkHairFolders();
    if (issues.length) {
      // eslint-disable-next-line no-console -- dev-only authoring aid, same as on import
      console.warn(`[character-editor] hair folder issues:\n${issues.map((s) => `  • ${s}`).join('\n')}`);
    }
  }
  return { ok: true, files: files.length, added, removed };
}

/** The folder hint shown by an empty picker, e.g. `assets/Dad/hair-back/…`. */
export const folderHint = (id: CharacterId, tail: string): string =>
  `assets/${CHARACTER_ASSETS[id].folder}/${tail}`;

/**
 * Pupil file stem for an `EYE_COLOURS` index on this character, or undefined.
 *
 * Pupils are matched BY EYE-COLOUR NAME, and a character's files may carry its own
 * suffix so they stay distinguishable outside the folder — Tamara renamed Dad's and
 * Mom's to `Blue-dad.png` / `Blue-mom.png` (2026-07-31) while the child's stayed
 * `Blue.png`. So an exact match wins first, then the same name with this
 * character's `-<id>` suffix. Only that character's own suffix is accepted: a blind
 * "strip everything after the last dash" would read `Light-Blue.png` as "Light".
 */
export const pupilStemForCharacter = (id: CharacterId, eyeIndex: number): string | undefined => {
  const name = EYE_COLOURS[eyeIndex]?.name.toLowerCase();
  if (!name) return undefined;
  const stems = CHARACTER_ASSETS[id].pupils.stems;
  return (
    stems.find((s) => s.toLowerCase() === name) ??
    stems.find((s) => s.toLowerCase() === `${name}-${id}`)
  );
};

// ── The CHILD's catalogues ────────────────────────────────────────────────────
// The head editor is the child's head, so it reads these directly. Every
// per-character consumer (the Animation tab) goes through CHARACTER_ASSETS.
export const HAIR_FRONT = CHARACTER_ASSETS.child.hairFront;
export const HAIR_BACK = CHARACTER_ASSETS.child.hairBack;
/** Hairstyle names present in either layer (the style gallery). */
export const HAIR_STYLES = CHARACTER_ASSETS.child.hairStyles;

/** Image slots that are NOT hair — used to detect the two hair slots by
 *  exclusion (their names carry the hairstyle, not the layer position). */
export const NON_HAIR_IMAGE_SLOTS = new Set<string>([
  EYE_PUPIL_ASSET,
  FRECKLES_ASSET,
  GLASSES_ASSET,
  // Body parts (fixes16+ added a body) — not hair, must not be mistaken for
  // hair slots by the exclusion-based detection below.
  'Body',
  'Body Preview',
  'Hips',
  'L Leg',
  'R Leg',
  'Foot',
  'Arm-template',
]);

export const FRECKLES_IMAGES = CHARACTER_ASSETS.child.freckles;
export const GLASSES_IMAGES = CHARACTER_ASSETS.child.glasses;
// Pupils: files NAMED BY EYE COLOUR (Blue.png, Green.png, … matching EYE_COLOURS
// names, case-insensitive). Selecting an eye colour auto-picks its file; a
// missing colour falls back to the .riv's base pupil.
export const PUPIL_IMAGES = CHARACTER_ASSETS.child.pupils;
export const pupilStemFor = (eyeIndex: number): string | undefined =>
  pupilStemForCharacter('child', eyeIndex);

/**
 * The AUTHORED colour palettes — `skin-sets.json` + `hair-sets.json`, the files the
 * head editor's Save buttons write — overlaid on the committed defaults.
 *
 * These are shared by every character: the palette is authoring data, only the
 * SELECTIONS differ per character. Read them here rather than falling back to
 * `CHARACTER_SKIN_PALETTE` / `HAIR_COLOURS`, or edits to the JSON reach whichever
 * character happens to be handed over from the head editor and no other (Tamara,
 * 2026-07-30: Dad ignored skin-sets.json for exactly that reason).
 *
 * The head editor keeps its own copies in component state because it can EDIT
 * them; read-only consumers use this. Never throws — a missing or broken file just
 * yields the committed/seeded values.
 */
export interface AuthoredPalettes {
  /** Per-tone skin sets, index-aligned with `CHARACTER_SKIN_PALETTE`. */
  skinSets: Record<string, string>[];
  /** Per-hair-colour layer ramps (bottom→top / dark→light) for `buildRecoloredHair`. */
  hairShades: string[][];
  /** Per-hair-colour eyebrow colour. */
  hairEyebrow: string[];
  /**
   * Per-hair-colour BEARD ramp — the same 5 bottom→top shades the hair uses, but
   * separately authored so a beard can read darker/greyer than the head hair.
   * Defaults to that colour's `hairShades` entry when `hair-sets.json` has no
   * `beard` for it, so an un-authored beard simply matches the hair.
   */
  beardShades: string[][];
}

export async function loadAuthoredPalettes(): Promise<AuthoredPalettes> {
  const out: AuthoredPalettes = {
    skinSets: CHARACTER_SKIN_PALETTE.map((p) => ({ ...p.set })),
    hairShades: HAIR_COLOURS.map((c) => seedShades(c.hex, HAIR_LAYER_COUNT)),
    hairEyebrow: HAIR_COLOURS.map((c) => c.eyebrow ?? c.hex),
    beardShades: HAIR_COLOURS.map((c) => seedShades(c.hex, HAIR_LAYER_COUNT)),
  };

  // skin-sets.json — per-index, per-key overrides of the committed set. Mirrors
  // the head editor's parse (entries may be `{ index, set: {...} }` or flat).
  try {
    const res = await fetch(SKIN_SETS_URL, { cache: 'no-store' });
    if (res.ok) {
      const json: unknown = await res.json();
      const list = Array.isArray(json) ? json : (json as { skinPalette?: unknown })?.skinPalette;
      if (Array.isArray(list)) {
        list.forEach((entry: unknown, i) => {
          const e = (entry ?? {}) as Record<string, unknown>;
          const raw = (typeof e.set === 'object' && e.set !== null ? e.set : e) as Record<string, unknown>;
          const idx = typeof e.index === 'number' ? e.index : i;
          if (!out.skinSets[idx]) return;
          for (const [k, v] of Object.entries(raw)) {
            if (typeof v === 'string' && k !== 'baseHex') out.skinSets[idx][k] = v;
          }
        });
      }
    }
  } catch {
    /* unreadable — committed palette stands */
  }

  // hair-sets.json — per-index shade ramp + eyebrow + beard ramp.
  try {
    const res = await fetch(HAIR_SETS_URL, { cache: 'no-store' });
    if (res.ok) {
      const json: unknown = await res.json();
      const list = Array.isArray(json) ? json : (json as { hairColours?: unknown })?.hairColours;
      if (Array.isArray(list)) {
        const hexes = (v: unknown): string[] =>
          Array.isArray(v) ? v.filter((s): s is string => typeof s === 'string') : [];
        list.forEach((entry: unknown, i) => {
          const e = (entry ?? {}) as Record<string, unknown>;
          const idx = typeof e.index === 'number' ? e.index : i;
          const shades = hexes(e.shades);
          const beard = hexes(e.beard);
          if (shades.length && out.hairShades[idx]) out.hairShades[idx] = shades;
          if (typeof e.eyebrow === 'string' && out.hairEyebrow[idx]) out.hairEyebrow[idx] = e.eyebrow;
          // An un-authored beard follows the HAIR ramp of the same colour (which may
          // itself be a file override) rather than the seeded one — a beard that
          // silently kept the preset hex while the hair was recoloured would read as
          // the wrong colour, not as "not authored yet".
          if (out.beardShades[idx]) out.beardShades[idx] = beard.length ? beard : shades.length ? shades : out.beardShades[idx];
        });
      }
    }
  } catch {
    /* unreadable — seeded ramps stand */
  }

  return out;
}

/** Every layered kind, i.e. the folders parsed by {@link parseLayeredHair}. */
const LAYERED_KINDS = ['hair-front', 'hair-back', 'beard'] as const;

/** Dev-only sanity checks for the per-character asset folders — surfaced to the
 *  console so naming mistakes are caught immediately when dropping files in. Flags:
 *   • a top-level folder that isn't a known character (e.g. art left at
 *     `assets/hair-front/…` after the per-character move — it would be invisible),
 *   • a character in the Scene with no folder of its own,
 *   • a style with a front stack but no back stack (or vice versa),
 *   • a layer numbered past the end of the shade ramp (it can't be coloured on its
 *     own and reuses the top shade). A GAP is fine — layers are shaded by name, so
 *     a stack of `_01,_03,_04,_05` simply doesn't use shade 2.
 *  A missing `beard/` folder is NOT flagged — only Dad is meant to have one. */
export function checkHairFolders(): string[] {
  const out: string[] = [];
  for (const stray of STRAY_FOLDERS) {
    out.push(
      `"assets/${stray}/…" is not a character folder — art must live in ` +
        `assets/<${CHARACTERS.map((c) => CHARACTER_ASSETS[c.id].folder).join('|')}>/<kind>/… or it is ignored`,
    );
  }
  for (const c of CHARACTERS) {
    const a = CHARACTER_ASSETS[c.id];
    if (c.present && !a.hairStyles.length) {
      out.push(`character "${c.id}" is in the Scene but has no hair in assets/${a.folder}/`);
    }
    for (const style of a.hairStyles) {
      const front = a.hairFront.byStyle[style];
      const back = a.hairBack.byStyle[style];
      if (front && !back) out.push(`${a.folder}: style "${style}" has a front stack but no hair-back/${style}/ stack`);
      if (back && !front) out.push(`${a.folder}: style "${style}" has a back stack but no hair-front/${style}/ stack`);
    }
    // Layers are shaded BY NAME, so a gap is harmless — but a layer numbered beyond
    // the ramp has no shade of its own and silently reuses the last one.
    for (const kind of LAYERED_KINDS) {
      const layered = kind === 'hair-front' ? a.hairFront : kind === 'hair-back' ? a.hairBack : a.beard;
      for (const style of layered.styles) {
        const over = layered.byStyle[style].filter((l) => l.n > HAIR_LAYER_COUNT).map((l) => l.n);
        if (over.length) {
          out.push(
            `${a.folder}: ${kind}/${style}/ has layer(s) _${over.join(', _')} but the ramp is only ` +
              `${HAIR_LAYER_COUNT} shades — they reuse shade ${HAIR_LAYER_COUNT}. Renumber them or ` +
              'raise HAIR_LAYER_COUNT.',
          );
        }
      }
    }
  }
  return out;
}

if (import.meta.env.DEV) {
  const issues = checkHairFolders();
  if (issues.length) {
    // eslint-disable-next-line no-console -- dev-only authoring aid for the character editor
    console.warn(`[character-editor] hair folder issues:\n${issues.map((s) => `  • ${s}`).join('\n')}`);
  }
}
